import type { ComponentProps } from 'react';
import { cn } from '@r/utils/utils.ts';

type PageContainerProps = ComponentProps<'div'> & {
  /**
   * `true` : pleine largeur + gouttière latérale (`px-5 md:px-7`), plafonné à
   * 2048px (classe `.app-shell`). Pour les pages avec sidebar (catalogue
   * produits, account) : pleine page sur écran courant, borné seulement à partir
   * des écrans 1440p / ultrawide.
   */
  wide?: boolean;
};

/**
 * Conteneur du contenu applicatif (zone entre header et footer).
 *
 * Rendu par **chaque page** autour de son contenu (pas dans le layout), pour que
 * la page choisisse son mode.
 *
 * Défaut — modèle « étagé » façon Bootstrap : largeur max figée par palier de
 * breakpoint (les marges grandissent entre deux paliers, puis « snap »). Paliers
 * calqués sur Bootstrap 5 (540 / 720 / 960 / 1140 / 1320). Plafond 1320px.
 * Gouttière (`px-5`) uniquement sous `sm` ; au-delà les marges auto respirent.
 *
 * `wide` — pleine largeur + gouttière `px-5 md:px-7`, plafond unique à 2048px
 * (partagé avec header et footer via `.app-shell`).
 *
 * L'espacement vertical reste géré par la page. Un enfant qui doit reprendre
 * toute la largeur du viewport (bandeau défilant) utilise la classe `full-bleed`.
 */
export function PageContainer({ className, wide = false, ...props }: PageContainerProps) {
  return (
    <div
      className={cn(
        wide
          ? 'app-shell px-5 md:px-7'
          : 'mx-auto w-full px-5 sm:px-0 sm:max-w-[540px] md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1140px] 2xl:max-w-[1320px]',
        className
      )}
      {...props}
    />
  );
}
